import { HttpClient } from '@angular/common/http';
import { JobSaveGateService } from './job-save-gate.service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { dayjs } from '@karve.it/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Action, Store, createSelector } from '@ngrx/store';
import { MutationOptionsAlone, QueryOptionsAlone } from 'apollo-angular/types';
import { partition, upperFirst } from 'lodash';
import {
  EMPTY,
  catchError,
  combineLatest,
  distinctUntilChanged,
  concat,
  concatMap,
  exhaustMap,
  filter,
  from,
  map,
  merge,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
  finalize,
  timeout,
} from 'rxjs';
import { CustomerEdit } from 'src/app/job/job-create/job-create-customer/job-create-customer.actions';
import { jobCreateCustomerFeature } from 'src/app/job/job-create/job-create-customer/job-create-customer.feature';
import { selectCustomerRole } from 'src/app/job/job-create/job-create-state/job-create.selectors';
import { shouldSetPendingServiceArea } from 'src/app/job/job-overview/overview-locations/overview-locations.reducer';
import { safeParseJSON } from 'src/app/js';
import { NewOpportunityCustomerActions } from 'src/app/new-opportunity/new-opportunity-customer/new-opportunity-customer.actions';
import { NewOpportunityLocationsActions } from 'src/app/new-opportunity/new-opportunity-locations/new-opportunity-locations.actions';
import { NewOpportunityActions } from 'src/app/new-opportunity/new-opportunity.actions';
import { LocationDetailsWizardActions } from 'src/app/shared/location-details-wizard/location-details-wizard.actions';
import { UnsavedChangesActions } from 'src/app/state/unsaved-changes.actions';

import {
  AvailableZonesGQL,
  AvailableZonesQueryVariables,
  ChargeUpdate,
  JobsV3PageGQL,
  JobsV3PageQuery,
  JobsV3PageQueryVariables,
  LocationCreateBase,
  PriceType,
  SetFieldValueInput,
  SingleEditInput,
  ResolveServiceAreaGQL,
  UpdateJobGQL,
  UpdateJobInput,
  UpdateJobMutation,
  UpdateJobMutationVariables,
  CreateUserInputWithRole,
  JobChangedGQL,
  JobDetailsV2Fragment,
  UpdateEventNavigatorGQL,
} from '../../generated/graphql.generated';
import { authFeature } from '../core/auth/auth.reducer';
import { generateMutationContext, getUniqueSessionID } from '../core/auth/session';
import { PlusAuthenticationService } from '../core/public-api';
import { zonesFeature } from '../core/zones/zones.reducer';
import { FIELD_CONFIG, JOB_LOCATION_TYPES_V3, JOB_ROLE_MAP } from '../global.constants';
import { quickJsActions } from '../quickjs/quickjs.actions';
import { quickjsFeature, selectScriptLoaded, selectCalculatedState, selectElements, selectUserInputs } from '../quickjs/quickjs.feature';
import { QuickJsService, RetriableError } from '../quickjs/quickjs.service';
import { AvailableZonesService } from '../services/available-zones.service';
import { FreyaHelperService } from '../services/freya-helper.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { UnsavedChangesBannerActions } from '../shared/unsaved-changes-banner/unsaved-changes-banner.actions';
import { selectJobPageChanges, selectJobSaveChanges } from '../shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { ActionSource } from '../state/action-sources';
import { brandingFeature, createConfigSelector } from '../state/branding.store';
import { selectUrl } from '../state/router';

import { selectJobDataForCalculator, selectSplitFranchises } from './job-calculator.selectors';
import { JobCreateInventoryActions } from './job-create/job-create-inventory/job-create-inventory.actions';
import { JobCreateLocationsActions } from './job-create/job-create-locations/job-create-locations-state.actions';
import { jobCreateSummaryFeature } from './job-create/job-create-summary/job-create-summary.feature';
import { summaryKeys } from './job-create/job-create-summary/job-create-summary.reducer';
import { JobFormActionButtonActions } from './job-create/job-form-action-button/job-form-action-button.actions';
import { JobManuallyAddLocationActions } from './job-create/job-manually-add-location/job-manually-add-location.actions';
import { selectCanChangeArea } from './job-edit/job-edit-state/job-edit.selectors';
import { JobHeaderActions } from './job-header/job-header.actions';
import { EMAIL_PATTERN, getDefaultLocationsForEventType, mapAdditionalContactsToInput, parseFullName } from './job-helpers';
import { InventoryActions } from './job-inventory/job-inventory.actions';
import { jobInventoryFeature } from './job-inventory/job-inventory.feature';
import { selectInventoryTotals } from './job-inventory/job-inventory.selectors';
import { InventoryItem, InventoryRoom, JobInventory, JobInventoryWithTotals } from './job-inventory/job-inventory.state';
import { JobInvoiceActions } from './job-invoice/job-invoice.actions';
import { LocationDetailsEditorKey, billingAddressChanged, encodeMetadataAccess, getLocationDetailsMetadataKey, resolveBillingAddressInput, resolveBillingLocationEditor } from './job-locations-helpers';
import { NavigatorOutboxActions } from './job-navigator/navigator-outbox/navigator-outbox.actions';
import { sendOrQueueNavigatorWrite } from './job-navigator/navigator-outbox/navigator-outbox.send';
import {
  InventoryChanges,
  inventoryChangesBetween,
  opsRevisionToSend,
  withoutInventoryChanges,
} from './job-navigator/navigator-outbox/navigator-outbox.util';
import { JobLocationsOverviewActions } from './job-overview/overview-locations/overview-locations.actions';
import { overviewLocationsFeature } from './job-overview/overview-locations/overview-locations.feature';
import { OverviewTimelineActions } from './job-overview/overview-timeline/overview-timeline.actions';
import { overviewTimelineFeature } from './job-overview/overview-timeline/overview-timeline.feature';
import { BookEventDialogActions } from './job-overview/overview-timeline/timeline-book-event/timeline-book-event.actions';
import { JobToolSubscriptionActions } from './job-state/job-tool-subscription.actions';
import { JobToolActions } from './job-tool.actions';
import {
  CALCULATOR_INPUT_METADATA_PREFIX,
  CALCULATOR_OUTPUT_METADATA_PREFIX,
  TRANSIENT_CALCULATOR_KEYS,
  selectCalculatorUrl,
  selectCustomer,
  selectJob,
  selectJobFranchiseId,
  selectJobId,
  selectJobLoaded,
} from './job-tool.reducer';
import { WorkOrdersActions } from './job-workorders/job-workorders.actions';
import { jobWorkordersFeature } from './job-workorders/job-workorders.feature';
import { selectEventUploadsInFlight } from './job-workorders/job-workorders.selectors';
import { resolveJobContextZone } from './job-zone.helpers';
import { LeaveConfirmationDialogActions } from './leave-job-confirm/leave-job-confirm.actions';

export const replayUnsavedChanges = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(
      JobToolActions.jobLoaded,
      JobToolSubscriptionActions.remoteJobUpdated,
    ),
    concatLatestFrom(() => store.select(selectJobPageChanges)),
    map(([ _action, changes ]) => UnsavedChangesActions.replayTriggered({ changes })),
  );
}, { functional: true, dispatch: true });

export const dispatchRemoteActions = createEffect((
  store = inject(Store),
  jobChangedGQL = inject(JobChangedGQL),
) => {
    return store.select(selectJobId).pipe(
      distinctUntilChanged(),
      switchMap((jobId) => !jobId ? EMPTY : jobChangedGQL.subscribe({ jobId }).pipe(
        map((val) => {

          const payload = val.data.job;
          const localAction = payload.sessionId === getUniqueSessionID();
          const actionLocation = localAction ? 'local' : 'remote';

          return {
            ...payload,
            type: `[${ActionSource.JOB_TOOL_SUBSCRIPTION}] ${actionLocation} ${payload.action}`,
          };
        }),
        tap((action) => store.dispatch(action)),
      )),
    );
}, { functional: true, dispatch: false });

export const fetchJob = createEffect(
  (
    actions$ = inject(Actions),
    jobsV3GQL = inject(JobsV3PageGQL),
    store = inject(Store),
    plusAuthService = inject(PlusAuthenticationService),
    availableZonesService = inject(AvailableZonesService),
  ) => {
    const jobIdChanged$ = store.select(selectJobId).pipe(
      filter(Boolean),
      map((jobId) => ({ jobId, isRetry: false, isBackground: false })),
    );

    const retryTriggered$ = actions$.pipe(
      ofType(JobToolActions.retryFetchJobTriggered),
      map(({ jobId }) => ({ jobId, isRetry: true, isBackground: false })),
    );

    // A re-read nobody asked for — a payment settling, a refund leg landing. It
    // takes the same path but reports its failure differently; see below.
    const backgroundTriggered$ = actions$.pipe(
      ofType(JobToolActions.backgroundRefreshTriggered),
      map(({ jobId }) => ({ jobId, isRetry: false, isBackground: true })),
    );

    const triggers$ = merge(jobIdChanged$, retryTriggered$, backgroundTriggered$);

    return triggers$.pipe(
      filter(({ jobId }) => Boolean(jobId)),
      mergeMap((trigger) => of(trigger).pipe(
        concatLatestFrom(() => store.select(selectJob)),
        map(([ { jobId, isRetry, isBackground }, job ]): {
          vars: JobsV3PageQueryVariables;
          opts: QueryOptionsAlone<JobsV3PageQueryVariables, JobsV3PageQuery>;
          jobId: string;
          isCached: boolean;
          isRetry: boolean;
          isBackground: boolean;
          searchedInZone: string;
        } => {
          const opts: QueryOptionsAlone<JobsV3PageQueryVariables, JobsV3PageQuery> = {
            fetchPolicy: 'network-only',
          };

          let requiresZoneChange = false;
          let requiredZone: string;

          if (job) {
            // Requirement: resolve through the job's shared zones, not just its
            // owning zone. An interstate job is owned by an area under
            // ymm-interstate-usa and shared with the franchise that works it, so
            // aiming at the owning zone asks for a context the user cannot enter
            // — which the backend silently swaps for an unrelated zone rather
            // than erroring. See resolveJobContextZone for the full rationale.
            requiredZone = resolveJobContextZone(
              job,
              plusAuthService.contextedZoneId,
              availableZonesService.availableZones.value,
            );
            requiresZoneChange = Boolean(requiredZone) && requiredZone !== plusAuthService.contextedZoneId;
          }

          let searchedInZone = plusAuthService.contextedZoneId;

          if (requiresZoneChange && !isRetry) {
            opts.context = {
              headers: {
                'x-zone': requiredZone,
              },
            };

            searchedInZone = requiredZone;
          }

          return {
            vars: {
              jobId,
              resolve: ['discounts', 'users', 'locations', 'distances', 'artifacts', 'charges'],
            },
            opts,
            jobId,
            isCached: Boolean(job),
            isRetry,
            isBackground,
            searchedInZone,
          };
        }),
        switchMap(({ vars, opts, jobId, isCached, isRetry, isBackground, searchedInZone }) => jobsV3GQL.fetch(vars, opts).pipe(
          exhaustMap((res) => {
            const { data: { jobs: { jobs } } } = res;
            const retry = !isRetry;

            // ⚠ These two are FAILURES TOO, and a background re-read must not
            // route off either.
            //
            // "Not found" emits `retry: true`, which `redirectToJobPage` skips
            // but `resolveJobZoneAndRetryFetchJob` picks up — and that either
            // switches the whole app's zone context or lands on a
            // `retry: false` that redirects. "Deleted" redirects immediately.
            // Reaching either from a poll fired by a settling payment would take
            // an operator off the job with a customer at the terminal, which is
            // the whole reason the background trigger exists.
            if (!jobs.length) {
              return of(isBackground
                ? JobToolActions.backgroundRefreshError({
                  jobId, error: new Error('Job not found'),
                })
                : JobToolActions.fetchJobError({
                  jobId,
                  error: new Error('Job not found'),
                  retry,
                  searchedInZone,
                  skipNotify: retry,
                }));
            }

            const [ job ] = jobs;

            if (job.deletedAt) {
              return of(isBackground
                ? JobToolActions.backgroundRefreshError({
                  jobId, error: new Error('Job deleted'),
                })
                : JobToolActions.fetchJobError({
                  jobId,
                  error: new Error('Job deleted'),
                  retry: false,
                  searchedInZone,
                  removeFromQuickAccess: true,
                }));
            }

            const successAction = JobToolActions.fetchJobSuccess({ job, isCached });
            // Requirement: the fetch has already succeeded here, so if the
            // current zone can see the job there is nothing to correct.
            // Switching away regardless is what broke 6797 — a working context
            // was traded for the job's unreachable owning zone.
            const requiredZone = resolveJobContextZone(
              job,
              plusAuthService.contextedZoneId,
              availableZonesService.availableZones.value,
            );

            if (!requiredZone || requiredZone === plusAuthService.contextedZoneId) {
              return of(successAction);
            }

            const setContextPromise = plusAuthService.setContext(requiredZone).then(() => successAction);

            return from(setContextPromise);
          }),
        )),
        catchError((error) => of(
          // ⚠ A BACKGROUND re-read must never redirect. `fetchJobError` drives
          // `redirectToJobPage`, which navigates away from the job — right when
          // someone opened a job that cannot be loaded, and badly wrong when a
          // poll blipped while an operator has a customer at the terminal. It
          // reports its own failure instead, and nothing routes off it: the
          // subscription and the next tick will try again.
          trigger.isBackground
            ? JobToolActions.backgroundRefreshError({ jobId: trigger.jobId, error })
            : JobToolActions.fetchJobError({ jobId: trigger.jobId, error, retry: false }),
        )),
      )),
    );
  },
  { functional: true, dispatch: true },
);

export const resolveJobZoneAndRetryFetchJob = createEffect(
  (
    actions$ = inject(Actions),
    availableZonesGQL = inject(AvailableZonesGQL),
    plusAuthService = inject(PlusAuthenticationService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.fetchJobError),
      filter(({ retry }) => retry),
      switchMap((action) => of(action).pipe(
        map(({ jobId, searchedInZone }): {
          jobId: string;
          searchedInZone: string;
          vars: AvailableZonesQueryVariables;
        } => ({
          jobId,
          searchedInZone,
          vars: {
            objectId: jobId,
            objectLabel: 'Job',
            limit: 1,
          },
        })),
        mergeMap(({ jobId, searchedInZone, vars }) => availableZonesGQL.fetch(vars).pipe(
          mergeMap((res) => {
            if (!res.data.availableZones?.length) {
              return of(JobToolActions.fetchJobError({
                jobId,
                error: new Error(`Cannot find job ${jobId} in any zone`),
                retry: false,
              }));
            }

            const [ jobZone ] = res.data.availableZones;
            const requiredZone = jobZone.type === 'area' && jobZone.parent ? jobZone.parent.id : jobZone.id;

            if (requiredZone === searchedInZone) {
              return of(JobToolActions.fetchJobError({
                jobId,
                error: new Error(`Report this bug: Cannot find job ${jobId} in assigned zone ${jobZone.name}`),
                retry: false,
              }));
            }

            return from(plusAuthService.setContext(requiredZone)).pipe(
              map(() => JobToolActions.retryFetchJobTriggered({ jobId })),
            );
          }),
        )),
        catchError((error) => of(JobToolActions.fetchJobError({
          jobId: action.jobId,
          error,
          retry: false,
        }))),
      )),
    );
  },
  { functional: true, dispatch: true },
);

export const redirectToJobPage = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  router = inject(Router),
) => {
  return actions$.pipe(
    ofType(
      JobToolActions.fetchJobError,
      JobToolActions.deleteJobSuccess,
    ),
    filter((action) => {
      if (action.type === JobToolActions.deleteJobSuccess.type) {
        return true;
      }

      return !action.retry;
    }),
    concatLatestFrom(() => [
      store.select(selectJobId),
      store.select(selectUrl),
    ]),
    filter(([ _action, jobId, url ]) => {
      const [ _blank, page, jobIdParam ] = url.split('/');

      // View lives at /jobs/:jobId, edit at /jobs/:jobId/edit — both carry the
      // job id in the second URL segment.
      return page === 'jobs' && jobId === jobIdParam;
    }),
    tap(() => router.navigate([ '/jobs' ], { queryParamsHandling: 'merge' })),
  );
}, { functional: true, dispatch: false });

export const dispatchJobLoadedFromServer = createEffect((
  actions$ = inject(Actions),
) => {
  return actions$.pipe(
    ofType(JobToolActions.fetchJobSuccess),
    map(({ job, isCached }) => JobToolActions.jobLoaded({
      job,
      source: isCached ? 'cache' : 'server',
    })),
  );
}, { functional: true, dispatch: true });

export const loadLocalJob = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(routerNavigatedAction),
      map(({ payload: { event: { urlAfterRedirects } } }) => new URL(urlAfterRedirects, window.location.origin).pathname),
      map((url) => {

        // View lives at /jobs/:jobId/...; edit at /jobs/:jobId/edit; create at /jobs/new.
        const isJobPage = url.startsWith('/jobs/') && !url.startsWith('/jobs/new');

        if (!isJobPage) {
          return {
            jobId: undefined,
            page: 'other',
          }
        }

        const [ jobId, path ] = url.split('/').slice(2);

        return {
          jobId,
          page: path === 'edit' ? 'edit' : 'view',
        };
      }),
      distinctUntilChanged((a, b) => a.jobId === b.jobId && a.page === b.page),
      filter((navigateSuccess) => navigateSuccess.page !== 'other'),
      concatLatestFrom(() => store.select(selectJob)),
      filter(([ _navigateSuccess, job ]) => Boolean(job)),
      map(([ _navigateSuccess, job ]) => JobToolActions.jobLoaded({
        job,
        source: 'cache',
      })),
    );
  },
  { functional: true, dispatch: true },
);

export const buildSaveChangesVariables = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
  plusAuthService = inject(PlusAuthenticationService),
  saveGate = inject(JobSaveGateService),
) => {
    return actions$.pipe(
      ofType(
        JobToolActions.saveChangesButtonClicked,
        JobHeaderActions.saveButtonClicked,
        JobHeaderActions.saveJobFooterClicked,
        LeaveConfirmationDialogActions.saveChangesButtonClicked,
        WorkOrdersActions.saveButtonClicked,
        WorkOrdersActions.addEstimateButtonClicked,
        JobInvoiceActions.createInvoiceButtonClicked,
        UnsavedChangesBannerActions.saveChangesButtonClicked,
        NewOpportunityActions.nextStepButtonClicked,
        NewOpportunityActions.previousStepButtonClicked,
        NewOpportunityActions.goToJobButtonClicked,
        // The crew's inventory edits no longer come this way: Confirm Scope sends them through the
        // navigator outbox (#86c). Once the merged list is on the phone, this saves the calculator
        // figures recalculated from it — and leaves the inventory out, which the server already has.
        quickJsActions.navigatorInventoryFiguresSaveRequested,
      ),
      concatMap((action) => {
        // The normal-save slot is claimed BEFORE the snapshot below is taken
        // and held until the mutation in `saveChanges` ends (CodeRabbit):
        // an attachment write squeezing between the snapshot and the save
        // used to leave the payload carrying the old list, and the
        // save-success rebuild then restored the removed attachment. With
        // slot + snapshot + mutation as one critical section, that can't
        // happen. `handedOff` marks the one exit where the slot belongs to
        // `saveChanges` (it releases in its finalize); every other exit
        // releases here.
        let handedOff = false;
        return store.select(selectEventUploadsInFlight).pipe(
        // The Event Notes 📎 upload flow keeps a per-event refcount while a
        // file is uploading; a save racing it would snapshot the attachment
        // list without the file and the save-success reload would lose it
        // (CodeRabbit: "Keep saves from overtaking an attachment upload").
        // Hold each save until no workorder upload is in flight.
        filter((uploads) => uploads === 0),
        take(1),
        switchMap(() => saveGate.acquire('normal')),
        mergeMap(() => of(action).pipe(
        concatLatestFrom(() => [
          // On a shared job the Crew Pay boxes are not ours to save: saveSharedCrewPay does.
          store.select(selectJobSaveChanges),
          store.select(selectJob),
          store.select(overviewLocationsFeature.selectLocationEditors),
          store.select(overviewLocationsFeature.selectUseEndLocationAsBillingLocation),
          store.select(overviewLocationsFeature.selectServiceArea),
          store.select(selectCanChangeArea),
          store.select(jobCreateSummaryFeature.selectSummaryEditors),
          store.select(jobInventoryFeature.selectInventoryEditor),
          store.select(selectInventoryTotals),
          store.select(jobWorkordersFeature.selectWorkordersEditor),
          store.select(selectUserInputs),
          store.select(selectCalculatedState),
          store.select(selectCalculatorUrl),
          store.select(overviewTimelineFeature.selectTimeline),
          store.select(jobCreateCustomerFeature.selectCustomerEditor),
          store.select(selectCustomer),
          store.select(selectCustomerRole),
          store.select(selectJobFranchiseId),
        ]),
        map(([ action, changes, ...rest ]) => [
          action,
          action.type === quickJsActions.navigatorInventoryFiguresSaveRequested.type
            ? changes.filter((c) => !NAVIGATOR_OWNED_INVENTORY_SOURCES.includes(c.source))
            : changes,
          ...rest,
        ] as const),
        // The figures save goes out with no change queued: the figures are what it saves.
        filter(([ action, changes ]) => action.type === quickJsActions.navigatorInventoryFiguresSaveRequested.type
          || changes.filter((c) => c.source !== ActionSource.ADD_INVENTORY).length > 0),
        map(([
          _action,
          changes,
          job,
          locationEditors,
          useEndLocationAsBillingLocation,
          serviceArea,
          canChangeArea,
          summaryEditors,
          inventoryEditor,
          inventoryTotals,
          workordersEditor,
          calculatorInputs,
          calculatorOutputs,
          calculatorUrl,
          timeline,
          customerEditor,
          currentCustomer,
          customerRoleId,
          jobFranchiseId,
        ]) => {

          const changeIds: string[] = [];

          const updateJobs: UpdateJobInput = {
            jobId: job.id,
            fields: {
              fields: [],
              objectLabel: 'Job',
            },
            metadata: {},
          }

          let customerEdit: CustomerEdit;

          const newOpportunityCustomerChanges = changes.filter((c) => c.source === ActionSource.NEW_OPPORTUNITY_CUSTOMER);

          if (newOpportunityCustomerChanges.length > 0) {

            const {
              selectedCustomer,
              fullName,
              phone,
              email,
              company,
              additionalContacts,
              referralSource,
              jobOrigin,
              customerType,
            } = customerEditor;

            if (email && !EMAIL_PATTERN.test(email)) {
              throw new Error('Invalid email format.');
            }

            if (selectedCustomer && selectedCustomer.id !== currentCustomer.id) {
              updateJobs.addUsers = [{
                userId: selectedCustomer.id,
                role: JOB_ROLE_MAP.customerRole,
              }];
            }

            if (!selectedCustomer || selectedCustomer.id !== currentCustomer.id) {
              updateJobs.removeUsers = [{
                userId: currentCustomer.id,
                role: JOB_ROLE_MAP.customerRole,
              }];
            }

            const { givenName, familyName } = parseFullName(fullName);

            const billingLocationEditor = resolveBillingLocationEditor(locationEditors, useEndLocationAsBillingLocation);

            const resolvedBillingAddress = resolveBillingAddressInput(locationEditors, useEndLocationAsBillingLocation);

            // Requirement: only a changed billing address is sent. The resolved value mirrors
            // either the end location or the billing editor, so it is populated on every save --
            // sending it unconditionally means a name-only edit re-creates the Location node and
            // re-points the customer's billing relationship. The customer being saved supplies
            // the comparison, which is the new one when the job customer is being swapped.
            const billingAddress = billingAddressChanged(
              resolvedBillingAddress,
              (selectedCustomer ?? currentCustomer)?.billingAddress,
            )
              ? resolvedBillingAddress
              : undefined;

            if (selectedCustomer) {

              updateJobs.setCustomerProfile = {
                familyName,
                givenName,
                phone,
                email,
                additionalContacts: mapAdditionalContactsToInput(additionalContacts ?? []),
              };

              if (company) {
                updateJobs.setCustomerProfile.company = company;
              }

              if (billingAddress) {
                updateJobs.setCustomerProfile.billingAddress = billingAddress;
              }

            } else if (fullName) {

              const newCustomerInput: CreateUserInputWithRole = {
                familyName,
                givenName,
                additionalContacts: mapAdditionalContactsToInput(additionalContacts ?? []),
                role: customerRoleId,
              }

              if (email) {
                newCustomerInput.email = email;
              }

              if (phone) {
                newCustomerInput.phone = phone;
              }

              if (company) {
                newCustomerInput.company = company;
              }

              if (billingAddress) {
                newCustomerInput.billingAddress = billingAddress;
              }

              updateJobs.newCustomers = [ newCustomerInput ];
            }

            updateJobs.fields.fields.push({
              fieldName: FIELD_CONFIG.customer.howHeard.name,
              value: referralSource,
            });

            updateJobs.metadata.jobOrigin = jobOrigin;

            updateJobs.metadata.jobType = customerType;

            // Variables snapshot to update local customer if mutation succeeds
            customerEdit = {
              selectedCustomer,
              givenName,
              familyName,
              phone,
              email,
              company,
              additionalContacts: additionalContacts?.filter((c) => !c.isUnlinked),
              selectedBillingLocation: billingLocationEditor?.selectedLocation,
            }

            changeIds.push(...newOpportunityCustomerChanges.map((c) => c.id));
          }

          const summaryChanges = changes.filter((c) => c.source === ActionSource.SUMMARY_OVERVIEW);

          if (summaryChanges.length > 0) {

            for (const key of summaryKeys) {
              updateJobs[key] = { contents: JSON.stringify(summaryEditors[key]) };
            }

            changeIds.push(...summaryChanges.map((c) => c.id));
          }

          const inventoryChanges = changes.filter((c) => [ ActionSource.INVENTORY, ActionSource.CREATE_INVENTORY ].includes(c.source));

          if (inventoryChanges.length > 0) {

            const inventory: JobInventory = {
              rooms: inventoryEditor.rooms,
              specialtyItems: inventoryEditor.specialtyItems,
              weightUnit: inventoryEditor.weightUnit,
              volumeUnit: inventoryEditor.volumeUnit,
            }

            // Not checking for undefined totals on purpose.
            // The eventual absense of these fields in the BE is relevant information and can be useful in future investigations.
            const inventoryWithTotals: JobInventoryWithTotals = {
              ...inventory,
              totalItems: inventoryTotals.totalItems,
              totalWeight: inventoryTotals.totalWeight,
              totalVolume: inventoryTotals.totalVolume,
            };

            const inventoryField: SetFieldValueInput = {
              fieldName: FIELD_CONFIG.jobs.inventory.name,
              value: JSON.stringify(inventoryWithTotals),
            }

            updateJobs.fields.fields.push(inventoryField);

            changeIds.push(...inventoryChanges.map((c) => c.id));
          }

          const additionalLocationChangeActionTypes: string[] = [
            NewOpportunityCustomerActions.googleAutocompleteAddressSelected,
            NewOpportunityCustomerActions.googleAutocompleteInputChanged,
            NewOpportunityCustomerActions.unitNumberEntered,
            NewOpportunityLocationsActions.googleAutocompleteAddressSelected,
            NewOpportunityLocationsActions.googleAutocompleteInputChanged,
            JobManuallyAddLocationActions.addLocationButtonClicked,
            LocationDetailsWizardActions.optionClicked,
          ].map((a) => a.type);

          const locationChanges = changes.filter(
            (c) => c.source === ActionSource.JOB_LOCATIONS_OVERVIEW || additionalLocationChangeActionTypes.includes(c.action.type),
          );

          if (locationChanges.length > 0) {

            updateJobs.addLocations = updateJobs.addLocations || [];

            for (const locationType of JOB_LOCATION_TYPES_V3) {

              if (!locationEditors || !(locationType in locationEditors)) { continue; }

              const editor = locationEditors[locationType];

              const { selectedLocation, unitNumber } = editor;

              if (!selectedLocation) { continue; }

              if (editor.locationModified) {

                const newLocationInput: LocationCreateBase = {
                  addressLineOne: selectedLocation.addressLineOne,
                  addressLineTwo: unitNumber,
                  areaCode: selectedLocation.areaCode,
                  city: selectedLocation.city,
                  country: selectedLocation.country,
                  countryJurisdiction: selectedLocation.countryJurisdiction,
                  locationType,
                  name: selectedLocation.name,
                  public: false,
                };

                if (selectedLocation.coordinates) {
                  newLocationInput.coordinates = {
                    latitude: selectedLocation.coordinates.latitude,
                    longitude: selectedLocation.coordinates.longitude,
                  }
                }

                updateJobs.addLocations.push({
                  locationType,
                  newLocationInput,
                });
              }

              if (!editor.fieldsModified) { continue; }

              if (locationType !== 'start' && locationType !== 'end') { continue; }

              const locationDetails: { key: LocationDetailsEditorKey; value: string }[] = [
                { key: 'buildingType', value: editor.buildingType },
                { key: 'size', value: editor.size },
                { key: 'access', value: encodeMetadataAccess(editor) },
              ];

              for (const detail of locationDetails) {
                updateJobs.metadata[getLocationDetailsMetadataKey(locationType, detail.key)] = detail.value || null;
              }

              const accessNotesFieldName = (FIELD_CONFIG[locationType] as typeof FIELD_CONFIG['start' | 'end']).accessNotes.name;

              updateJobs.fields.fields.push({
                fieldName: accessNotesFieldName,
                value: editor.accessNotes,
              });
            }

            updateJobs.removeLocations = job.locations
              .filter((l) => locationEditors && !(l.locationType in locationEditors))
              .map((l) => l.locationId);

            changeIds.push(...locationChanges.map((c) => c.id));
          }

          if (serviceArea && canChangeArea) {
            updateJobs.setZone = serviceArea.id;
          }

          if (!updateJobs.fields.fields.length) {
            delete updateJobs.fields;
          }

          const workorderChanges = changes.filter((c) => c.source === ActionSource.WORKORDERS);

          // New workorders cannot carry metadata in the addEvents input
          // (the schema has no metadata there), so attachment lists attached
          // BEFORE the first save are persisted with a follow-up
          // editCalendarEvents mutation after the create (CodeRabbit:
          // "Persist attachments when saving a new event").
          let pendingNewEventAttachments: Array<{ eventId: string; eventAttachments: unknown[] }> = [];

          if (workorderChanges.length > 0) {

            const nonRemovedWorkorders = workordersEditor.workorders
              .filter((wo) => !wo.removed);

            const workordersToAdd = workordersEditor.workorders.filter((wo) => wo.new && !wo.removed);
            // Note: removed location validation to allow building estimates before adding locations
            // for (const wo of workordersToAdd) {
            //   const validation = validateLocationsForEventType(job?.locations || [], wo.type);
            //   if (!validation.isValid) {
            //     throw new Error(`Cannot add event "${wo.title}": ${validation.errorMessage}`);
            //   }
            // }

            updateJobs.addEvents = workordersToAdd
              .map((wo) => ({
                id: wo.eventId,
                sequentialOrder: nonRemovedWorkorders.findIndex((nrwo) => nrwo.eventId === wo.eventId),
                status: 'required',
                title: wo.title,
                shortTitle: upperFirst(wo.type),
                locations: getDefaultLocationsForEventType(job?.locations, wo.type),
                type: wo.type,
              }));

            pendingNewEventAttachments = workordersToAdd
              .filter((wo) => wo.eventAttachments !== undefined || Boolean(wo.eventNotes))
              .map((wo) => ({
                eventId: wo.eventId,
                eventAttachments: wo.eventAttachments as unknown[],
                eventNotes: wo.eventNotes || undefined,
              }));

            updateJobs.removeCalendarEvents = workordersEditor.workorders
              .filter((wo) => !wo.new && wo.removed)
              .map((wo) => wo.eventId);

            updateJobs.editCalendarEvents = {
              edits: workordersEditor.workorders
              .filter((wo) => !wo.new && !wo.removed && (wo.modified || wo.cancelled))
              .map((wo): SingleEditInput => {

                if (wo.cancelled) {
                  return {
                    id: wo.eventId,
                    edit: {
                      status: 'cancelled',
                    }
                  }
                }

                return {
                  id: wo.eventId,
                  edit: {
                    sequentialOrder: nonRemovedWorkorders.findIndex((nrwo) => nrwo.eventId === wo.eventId),
                    // Event notes merged from a mid-save edit + files
                    // attached to THE event via the Event Notes 📎 (#6983).
                    // eventAttachments only when touched — the backend merges
                    // metadata key-level, so untouched keys never clobber.
                    metadata: JSON.stringify({
                        eventNotes: wo.eventNotes,
                        ...(wo.eventAttachments !== undefined
                            ? { eventAttachments: wo.eventAttachments }
                            : {}),
                    }),
                  },
                }
              }),
            }

            const charges = nonRemovedWorkorders
              .flatMap(wo => wo.charges.map(c => ({ ...c, workorderId: wo.eventId })));

            const chargesToAdd = charges
            .filter((charge) => charge.new && !charge.removed)
              .map((charge) => {

                const workorderIndex = nonRemovedWorkorders
                  .findIndex((nrwo) => nrwo.eventId === charge.workorderId);

                const chargeIndex = nonRemovedWorkorders[workorderIndex].charges
                  .filter((c) => !c.removed)
                  .findIndex((c) => c.chargeId === charge.chargeId);

                const amount = charge.priceType === PriceType.Percentage
                  ? charge.amount
                  : Math.round(charge.amount * 100);

                return {
                  id: charge.chargeId,
                  eventId: charge.workorderId,
                  productId: charge.productId,
                  priceId: charge.priceId,
                  description: charge.description,
                  productName: charge.productName,
                  quantity: charge.quantity,
                  amount,
                  order: (workorderIndex * 100) + chargeIndex,
                  currency: job.currency,
                  taxIds: charge.taxes.map((t) => t.id),
                  attributes: charge.attributes?.filter(Boolean),
                }
              });

            if (chargesToAdd.length > 0) {
              updateJobs.newCharges = {
                userId: plusAuthService.user.id,
                charges: chargesToAdd,
              }
            }

            const chargesToUpdate: ChargeUpdate[] = charges
              .filter((charge) => !charge.new && !charge.removed && charge.modified)
              .map((charge) => {

                const workorderIndex = nonRemovedWorkorders
                  .findIndex((nrwo) => nrwo.eventId === charge.workorderId);

                const chargeIndex = nonRemovedWorkorders[workorderIndex].charges
                  .filter((c) => !c.removed)
                  .findIndex((c) => c.chargeId === charge.chargeId);

                const amount = charge.priceType === PriceType.Percentage
                  ? charge.amount
                  : Math.round(charge.amount * 100);

                return {
                  eventId: charge.workorderId,
                  id: charge.chargeId,
                  quantity: charge.quantity,
                  description: charge.description,
                  amount,
                  order: (workorderIndex * 100) + chargeIndex,
                }
            });

            if (chargesToUpdate.length > 0) {
              updateJobs.updateCharges = {
                charges: chargesToUpdate,
              }
            }

            updateJobs.removeCharges = charges
              .filter((c) => !c.new && c.removed)
              .map((c) => c.chargeId);

            const discounts = nonRemovedWorkorders
              .flatMap((wo) => wo.discounts.map((d) => ({ ...d, workorderId: wo.eventId})));

            const [ presetDiscounts, customDiscounts ] = partition(discounts, (d) => Boolean(d.discountId));

            updateJobs.addDiscounts = presetDiscounts
              .filter((d) => d.new && !d.removed)
              .map((d) => ({
                eventId: d.workorderId,
                discountId: d.discountId,
                customAmount: d.customAmount,
              }));

            updateJobs.createDiscounts = customDiscounts
              .filter((d) => d.new && !d.removed)
              .map((d) => ({
                eventsIds: [ d.workorderId ],
                discountInput: {
                  active: true,
                  name: d.name,
                  code: d.code,
                  maxRedemptions: 1,
                  amount: d.customAmount,
                  discountType: d.discountType,
                  attributes: [ 'single-use' ],
                },
              }));

            updateJobs.removeDiscounts = discounts
              .filter((d) => !d.new && d.removed)
              .map((d) => d.appliedId);

            changeIds.push(...workorderChanges.map((c) => c.id));
          }

          const timelineChanges = changes.filter((c) => c.source === ActionSource.OVERVIEW_TIMELINE);

          if (timelineChanges.length > 0) {

            const [ startDate, endDate ] = timeline;

            const start = startDate ? dayjs(startDate) : undefined;

            if (start) {
              updateJobs.timeline = start.format('YYYY-MM-DD');
            }

            if (start && endDate) {
              const end = dayjs(endDate);
              updateJobs.timelineDays = end.diff(start, 'days');
            } else if (start) {
              updateJobs.timelineDays = 0;
            }

            changeIds.push(...timelineChanges.map((c) => c.id));
          }

          const calculatorChanges = changes.filter((c) => c.source === ActionSource.QUICK_JS);

          // Update inputs only if inputs changed.
          if (calculatorChanges.length > 0) {

            for (const [ key, value ] of Object.entries(calculatorInputs)) {
              if (TRANSIENT_CALCULATOR_KEYS.has(key)) { continue; }
              updateJobs.metadata[CALCULATOR_INPUT_METADATA_PREFIX + key] = value;
            }

            changeIds.push(...calculatorChanges.map((c) => c.id));
          }

          // Update outputs regardless
          for (const [ key, value ] of Object.entries(calculatorOutputs)) {

            const allowed = [ 'string', 'number', 'boolean' ];

            if (!(allowed.includes(typeof value))) { continue; }

            updateJobs.metadata[CALCULATOR_OUTPUT_METADATA_PREFIX + key] = value;
          }

          if (calculatorUrl) {
            updateJobs.metadata.calculatorUrl = calculatorUrl;
          }

          return {
            vars: { updateJobs },
            jobFranchiseId,
            changeIds,
            promptedBy: action,
            customerEdit,
            pendingNewEventAttachments,
          };
        }),
        map((payload) => {
          // From this emission on, `saveChanges` owns the slot — it releases
          // when the mutation completes, errors or the effect is torn down.
          handedOff = true;
          return JobToolActions.buildSaveChangesVariablesSuccess(payload);
        }),
        catchError((error) => {
          saveGate.endNormalSave();
          return of(JobToolActions.buildSaveChangesVariablesError({ error }));
        }),
        finalize(() => { if (!handedOff) { saveGate.endNormalSave(); } }),
      )));
      }),
    );

  }, { functional: true, dispatch: true });

export const saveChanges = createEffect((
  actions$ = inject(Actions),
  updateJobGQL = inject(UpdateJobGQL),
  plusAuthService = inject(PlusAuthenticationService),
  notify = inject(FreyaNotificationsService),
  saveGate = inject(JobSaveGateService),
) => {
  return actions$.pipe(
    ofType(JobToolActions.buildSaveChangesVariablesSuccess),
    // concatMap, not exhaustMap: a queued save must still run after the
    // previous one completes (CodeRabbit). The normal-save slot is already
    // held — claimed by buildSaveChangesVariables BEFORE its snapshot — so
    // the snapshot and this mutation are one critical section: no 📎 upload,
    // attachment removal or ops write can interleave (CodeRabbit).
    concatMap((payload) => {
        const { vars, jobFranchiseId, changeIds, promptedBy, customerEdit, pendingNewEventAttachments } = payload;
        const updateJobInput = Array.isArray(vars.updateJobs)
        ? vars.updateJobs[0]
        : vars.updateJobs;

      const locationsChanged = Boolean(
        updateJobInput?.addLocations?.length ||
        updateJobInput?.removeLocations?.length,
      );

      // Event Notes 📎 on workorders created by THIS save: addEvents cannot
      // carry metadata, so persist the lists right after the create with a
      // minimal editCalendarEvents batch (CodeRabbit).
      const jobId = (updateJobInput as { jobId?: string } | undefined)?.jobId;
      const attachmentFollowUp$ = pendingNewEventAttachments?.length && jobId
        ? updateJobGQL.mutate({
            updateJobs: {
              jobId,
              editCalendarEvents: {
                // JSON.stringify drops undefined values, so notes-only and
                // attachments-only events carry just their own additions.
                edits: pendingNewEventAttachments.map(({ eventId, eventAttachments, eventNotes }) => ({
                  id: eventId,
                  edit: {
                    metadata: JSON.stringify({ eventAttachments, eventNotes }),
                  },
                })),
              },
            },
          } as any, {
            context: generateMutationContext({ zone: jobFranchiseId }),
          })
        : null;

      // The normal-save slot was claimed atomically by acquire() above
      // (ops writes and 📎 uploads must not start inside it); released on
      // complete, error AND unsubscribe via finalize.
      return updateJobGQL.mutate(vars, {
        context: generateMutationContext({ zone: jobFranchiseId }),
      }).pipe(
        // A follow-up failure must not fail the ALREADY-completed main save,
        // but it must be VISIBLE: the save's rebuild discards the pending
        // list, so the user has to re-attach (CodeRabbit).
        // Gate: only a SUCCESSFUL main save gets the follow-up — an
        // unsuccessful save's events were never created, and persisting
        // attachments onto events that don't exist would fail pointlessly
        // (CodeRabbit).
        concatMap((res) => attachmentFollowUp$ && res.data?.updateJobs?.isSuccess === true
          ? attachmentFollowUp$.pipe(catchError((error) => {
              console.error('Event attachment follow-up failed', error);
              notify.error('Event attachments could not be saved', 'Re-attach the files and save again.');
              return of(res);
            }), map(() => res))
          : of(res)),
        // NOTE: the MAIN save's response flows on throughout — the follow-up's
        // own response is deliberately discarded (only saveChangesSuccess's
        // updatedJob matters, and it comes from the main save below).
        exhaustMap((res) => {
          const jobZone = res.data.updateJobs.jobs[0].zone;
          const requiredZone = jobZone.type === 'area' && jobZone.parent ? jobZone.parent.id : jobZone.id;

          if (requiredZone === plusAuthService.contextedZoneId) {
            return of(res);
          }

          const setContextPromise = plusAuthService.setContext(requiredZone).then(() => res);

          return from(setContextPromise);
        }),
        map((res) => JobToolActions.saveChangesSuccess({
          updatedJob: res.data.updateJobs.jobs[0],
          changeIds,
          promptedBy,
          locationsChanged,
          customerEdit,
        })),
        catchError((error) => of(JobToolActions.saveChangesError({
          error,
          changeIds,
        }))),
        finalize(() => saveGate.endNormalSave()),
      );
    }),
  );
}, { functional: true, dispatch: true });

/** The unsaved-change sources of the inventory editor — what the navigator's Confirm Scope sends (#86c). */
const NAVIGATOR_OWNED_INVENTORY_SOURCES = [ ActionSource.INVENTORY, ActionSource.CREATE_INVENTORY ];

/** How long the figures save waits for the recalculation of a merged list before it saves anyway. */
const MERGED_INVENTORY_RECALC_WAIT_MS = 5_000;

/** The job's stored inventory value, from this device's copy of the job. */
function storedInventoryValue(job: JobDetailsV2Fragment | null | undefined): string | null {
  const field = job?.fields?.find((f) => f.name === FIELD_CONFIG.jobs.inventory.name);
  return field?.values?.[0]?.value ?? null;
}

/** The same job with its inventory field holding `value` — the only change. */
function withInventoryValue(job: JobDetailsV2Fragment, value: string): JobDetailsV2Fragment {
  const fields = job.fields ?? [];
  const index = fields.findIndex((f) => f.name === FIELD_CONFIG.jobs.inventory.name);
  if (index < 0) {
    return { ...job, fields: [ ...fields, { name: FIELD_CONFIG.jobs.inventory.name, values: [ { value } ] } as typeof fields[number] ] };
  }
  const field = fields[index];
  const values = [ { ...field.values?.[0], value }, ...(field.values ?? []).slice(1) ] as typeof field.values;
  return { ...job, fields: fields.map((f, i) => (i === index ? { ...f, values } : f)) };
}

/**
 * Confirm Scope (#86c): the crew's inventory changes go to the server as a navigator save through
 * the outbox, per item — what the crew started from (the list as this device last had it from the
 * server) and what they set — with the review marks in the same write, so the #97 missing pair
 * lands together. With no signal it waits, survives a restart, and is sent from anywhere when
 * signal returns; the server merges it item by item, keeping the office's version of any item the
 * office changed meanwhile.
 *
 * This device's copy of the job takes the crew's list at once, and the edits leave the job page's
 * unsaved changes: the outbox owns them now, and a reload while they wait still shows them.
 */
export const sendNavigatorScopeConfirm = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateEventNavigatorGQL = inject(UpdateEventNavigatorGQL),
) => {
  return actions$.pipe(
    ofType(quickJsActions.navigatorScopeConfirmButtonClicked),
    concatLatestFrom(() => [
      store.select(selectJob),
      store.select(jobInventoryFeature.selectInventoryEditor),
      store.select(selectInventoryTotals),
      store.select(selectJobPageChanges),
    ]),
    concatMap(([ { eventId, inventoryReview }, job, inventoryEditor, inventoryTotals, pageChanges ]) => {
      const jobId: string | null = job?.id ?? null;
      const base = safeParseJSON(storedInventoryValue(job), {} as Partial<JobInventory>).rooms ?? [];
      const changes = job ? inventoryChangesBetween(base, inventoryEditor.rooms) : null;

      // Also when the edits cancel out (+1, then -1): they still leave the job page's unsaved changes.
      const changeIds = pageChanges.filter((c) => NAVIGATOR_OWNED_INVENTORY_SOURCES.includes(c.source)).map((c) => c.id);
      const local: Action[] = [];
      if (job && (changes || changeIds.length)) {
        const inventory: JobInventoryWithTotals = {
          rooms: inventoryEditor.rooms,
          specialtyItems: inventoryEditor.specialtyItems,
          weightUnit: inventoryEditor.weightUnit,
          volumeUnit: inventoryEditor.volumeUnit,
          totalItems: inventoryTotals.totalItems,
          totalWeight: inventoryTotals.totalWeight,
          totalVolume: inventoryTotals.totalVolume,
        };
        local.push(NavigatorOutboxActions.jobInventoryUpdatedLocally({
          jobId: job.id,
          job: withInventoryValue(job, JSON.stringify(inventory)),
          changeIds,
        }));
      }

      const event = job?.events?.find((e) => e.id === eventId);
      const send = sendOrQueueNavigatorWrite(store, updateEventNavigatorGQL, jobId, {
        eventId,
        navigatorData: JSON.stringify({ inventoryReview }),
        basedOnOpsRevision: opsRevisionToSend(event, undefined),
        ...(changes ? { inventoryChanges: JSON.stringify(changes) } : {}),
      }, {
        stored: (stored) => [
          ...(jobId && stored != null
            ? [ WorkOrdersActions.navigatorDataUpdatedLocally({ jobId, eventId, navigatorData: stored }) ]
            : []),
          quickJsActions.patchNavigatorDataSuccess(),
        ],
        refused: (error) => [
          ...(changes ? [ NavigatorOutboxActions.inventorySaveRefused({ jobId, eventId, changes }) ] : []),
          quickJsActions.patchNavigatorDataError({ error }),
        ],
      });

      return concat(from(local), send);
    }),
  );
}, { functional: true, dispatch: true });

/** The editor actions that make the crew's inventory changes again — recorded as unsaved edits. */
function inventoryChangeActions(changes: InventoryChanges): Action[] {
  const actions: Action[] = [];
  for (const room of changes.rooms) {
    if (!room.after) {
      // Deleted room: remove it again as an unsaved edit (CodeRabbit —
      // empty-room deletions ride as a room change with after: null).
      if (room.before) { actions.push(InventoryActions.removeRoomButtonClicked({ roomId: room.id })); }
      continue;
    }
    actions.push(room.before
      ? InventoryActions.editRoomNameInPlaceConfirmButtonClicked({ roomId: room.id, name: room.after.name })
      : InventoryActions.roomAdded({ room: { ...room.after, id: room.id, inventory: [] } as InventoryRoom }));
  }
  for (const item of changes.items) {
    if (!item.after) {
      actions.push(InventoryActions.removeItemButtonClicked({ roomId: item.roomId, itemId: item.id }));
    } else if (!item.before) {
      actions.push(InventoryActions.itemAdded({ roomId: item.roomId, item: item.after as InventoryItem }));
    } else {
      actions.push(InventoryActions.itemEdited({ roomId: item.roomId, item: item.after as InventoryItem }));
    }
  }
  return actions;
}

/**
 * The server refused the crew's inventory save (#86c). Confirm Scope had already put the crew's list
 * in this device's copy of the job and taken the edits out of the unsaved changes, so as things
 * stood the next Confirm Scope found nothing to send, and the next update from the server wiped
 * the edits.
 *
 * Instead the copy goes back to the list the server has, and the crew's changes are made again in
 * the editor, as unsaved edits: on screen as before, kept through an update from the server like any
 * unsaved edit, and sent by the next Confirm Scope. Edits made since are made again after them, in
 * order. Only for the job open on this device: the editor holds one job at a time (on any other page →
 * `reportRefusedInventoryElsewhere`, in the outbox's effects).
 */
export const restoreRefusedNavigatorInventory = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(NavigatorOutboxActions.inventorySaveRefused),
    concatLatestFrom(() => [ store.select(selectJob), store.select(selectJobPageChanges) ]),
    filter(([ { jobId }, job ]) => !!jobId && job?.id === jobId),
    concatMap(([ { changes }, job, pageChanges ]) => {
      const since = pageChanges.filter((c) => NAVIGATOR_OWNED_INVENTORY_SOURCES.includes(c.source));
      const stored = safeParseJSON(storedInventoryValue(job), {} as Partial<JobInventory>);
      const rooms = withoutInventoryChanges(stored.rooms ?? [], changes);
      return from([
        NavigatorOutboxActions.jobInventoryUpdatedLocally({
          jobId: job.id,
          job: withInventoryValue(job, JSON.stringify({ ...stored, rooms })),
          changeIds: since.map((c) => c.id),
        }),
        ...inventoryChangeActions(changes),
        ...since.map(({ action }) => {
          const { metadata: _replayed, ...again } = action as Action & { metadata?: unknown };
          return again;
        }),
      ]);
    }),
  );
}, { functional: true, dispatch: true });

/**
 * The crew's inventory save landed (#86c): load the list the server kept into this device's copy of
 * the job and the inventory editor, which recalculates the calculator figures from it, then save
 * those figures with a regular job save — so they always match the list the server stored, whether
 * the save went at once or after signal returned.
 *
 * Only for the job open on this device: the editor holds one job at a time. Inventory edits the crew
 * has made since (after "Make changes") are re-applied on top, and the figures then wait for the
 * next Confirm Scope, which saves them with those edits.
 */
export const loadMergedNavigatorInventory = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(NavigatorOutboxActions.inventoryMerged),
    concatLatestFrom(() => [ store.select(selectJob), store.select(selectJobPageChanges) ]),
    filter(([ { jobId, inventory }, job ]) => !!jobId && inventory !== null && job?.id === jobId),
    concatMap(([ { inventory }, job, pageChanges ]) => {
      const pending = pageChanges.filter((c) => NAVIGATOR_OWNED_INVENTORY_SOURCES.includes(c.source));
      const load = from([
        NavigatorOutboxActions.jobInventoryUpdatedLocally({ jobId: job.id, job: withInventoryValue(job, inventory ?? ''), changeIds: [] }),
        ...(pending.length ? [ UnsavedChangesActions.replayTriggered({ changes: pending }) ] : []),
      ]);
      if (pending.length) { return load; }

      // Subscribed before the load, so it sees the recalculation the load sets off.
      const figures = actions$.pipe(
        ofType(quickJsActions.runCalculationsSuccess, quickJsActions.runCalculationsError),
        take(1),
        timeout({ first: MERGED_INVENTORY_RECALC_WAIT_MS, with: () => of(null) }),
        // A success means the figures were recalculated from the merged list — save
        // them. An error or the wait timing out means what's on hand can predate the
        // merge, so don't save those; keep a pending save that runs when the next
        // recalculation succeeds (figures then match the list the server stored).
        switchMap((settled) =>
          settled !== null && settled.type === quickJsActions.runCalculationsSuccess.type
            ? of(quickJsActions.navigatorInventoryFiguresSaveRequested({ jobId: job.id }))
            : actions$.pipe(
                ofType(quickJsActions.runCalculationsSuccess),
                take(1),
                map(() => quickJsActions.navigatorInventoryFiguresSaveRequested({ jobId: job.id })),
              ),
        ),
      );
      return merge(figures, load);
    }),
  );
}, { functional: true, dispatch: true });

export const loadJobLocations = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobToolActions.jobLoaded,
        JobToolActions.jobDataReset,
        JobToolActions.saveChangesSuccess,
        JobLocationsOverviewActions.deleteJobLocationSuccess,
      ),
      concatLatestFrom(() => [
        store.select(selectJob),
      ]),
      filter(([ _action, job ]) => Boolean(job)),
      map(([ promptedBy, job ]) => JobCreateLocationsActions.jobLocationsLoaded({ promptedBy, job })),
    );
  }, { functional: true, dispatch: true });

export const resetJobData = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobToolActions.discardUnsavedChangesButtonClicked,
        LeaveConfirmationDialogActions.discardUnsavedChangesButtonClicked,
        UnsavedChangesBannerActions.discardUnsavedChangesButtonClicked,
        NewOpportunityActions.discardChangesBeforeNavigatingToJobPageConfirmed,
      ),
      concatLatestFrom(() => [
        store.select(selectJob),
        store.select(selectUrl),
      ]),
      filter(([ _action, _job, url ]) => !url.startsWith('/jobs/new')),
      map(([ _action, job ]) => JobToolActions.jobDataReset({ job })),
    );
  }, { functional: true, dispatch: true });

export const openSelectServiceAreaDialog = createEffect((
  actions$ = inject(Actions),
  freyaHelper = inject(FreyaHelperService),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobLocationsOverviewActions.changeAreaButtonClicked,
        JobCreateLocationsActions.setServiceAreaButtonClicked,
        JobHeaderActions.changeAreaMenuItemClicked,
        WorkOrdersActions.setAreaButtonClicked,
      ),
      switchMap((action) => from(freyaHelper.openSelectAreaDialog(({
        actionRequired: action.type === JobCreateLocationsActions.setServiceAreaButtonClicked.type,
        onlyShowCurrentSubzones: false,
      }))).pipe(
        filter((serviceArea) => Boolean(serviceArea)),
        concatLatestFrom(() => store.select(selectJob)),
        map(([ serviceArea, job ]) => JobLocationsOverviewActions.serviceAreaSelected({
          serviceArea,
          promptedBy: action,
          hasJob: Boolean(job),
        })),
      ),
    ));
}, { functional: true, dispatch: true });

export const changeArea = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
  plusAuthService = inject(PlusAuthenticationService),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.serviceAreaSelected),
      filter((payload) => !shouldSetPendingServiceArea(payload)),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJobId),
          store.select(selectJobFranchiseId)
        ]),
        map(([ { serviceArea }, jobId, jobFranchiseId ]): {
          vars: UpdateJobMutationVariables;
          opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables>;
        } => {
          return {
            vars: { updateJobs: { jobId, setZone: serviceArea.id, syncCalculatorFromConfig: true } },
            opts: { context: generateMutationContext({
              zone: jobFranchiseId,
            }) },
          }
        }),
        exhaustMap(({ vars, opts }) => updateJobGQL.mutate(vars, opts).pipe(
          exhaustMap((res) => {

            const jobZone = res.data.updateJobs.jobs[0].zone;

            const requiredZone = jobZone.type === 'area' && jobZone.parent ?
              jobZone.parent.id : jobZone.id;

            if (requiredZone === plusAuthService.contextedZoneId) { return of(res) }

            const setContextPromise = plusAuthService.setContext(requiredZone).then(() => res);

            return from(setContextPromise);
          }),
          map((res) => JobToolActions.updateJobAreaSuccess({ updatedJob: res.data.updateJobs.jobs[0] })),
        )),
        catchError((error) => of(JobToolActions.updateJobAreaError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const resolveServiceAreaAfterLocationAutocompleted = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  resolveServiceAreaGQL = inject(ResolveServiceAreaGQL),
) => {
    return actions$.pipe(
      ofType(
        JobCreateLocationsActions.googleAutocompleteAddressSelected,
        JobLocationsOverviewActions.googleAutocompleteAddressSelected,
        NewOpportunityCustomerActions.googleAutocompleteAddressSelected,
        NewOpportunityLocationsActions.googleAutocompleteAddressSelected,
      ),
      switchMap((action) => of(action).pipe(
        concatLatestFrom(() => store.select(createSelector(
          overviewLocationsFeature.selectLocationEditors,
          (editors) => ({
            start: editors['start']?.selectedLocation,
            end: editors['end']?.selectedLocation,
          })
        ))),
        filter(([ _action, { start } ]) => Boolean(start)),
        mergeMap(([ _action, { start, end }]) => resolveServiceAreaGQL.fetch({
          areaCode: start.areaCode,
          query: start.addressLineOne,
          endLocationAreaCode: end?.areaCode,
          endLocationQuery: end?.addressLineOne,
        }).pipe(
          map((res) => JobCreateLocationsActions.resolveServiceAreaSuccess({ responseData: res.data.resolveServiceArea })),
        )),
        catchError((error) => of(JobCreateLocationsActions.resolveServiceAreaError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const fetchScript = createEffect((
  store = inject(Store),
  httpClient = inject(HttpClient),
) => {
    // Readiness (job loaded, config loaded) is folded into the source stream
    // rather than sampled off the URL, so a readiness transition re-triggers
    // evaluation even when the URL stays undefined. Otherwise the missing-URL
    // branch would never fire: the URL never changes from undefined, so nothing
    // downstream would re-run once loading completes.
    return combineLatest([
      store.select(selectCalculatorUrl),
      store.select(selectJobLoaded),
      store.select(selectJobId),
      store.select(brandingFeature.selectLoading),
    ]).pipe(
      filter(([ scriptUrl, jobLoaded, jobId, configLoading ]) => {
        // Requirement: Wait for the job to load before acting when a job is expected, so a
        // job-specific calculator URL doesn't get clobbered by the config
        // default. When there's no jobId (e.g. new-opportunity flow), there's
        // no job to wait for.
        if (!(jobLoaded || !jobId)) { return false; }

        // With the job settled, a resolved URL is safe to act on immediately.
        if (scriptUrl) { return true; }

        // No URL resolved yet. `selectCalculatorUrl` reads from both the job
        // metadata and the zone config (`job.metadata.calculatorUrl || config`),
        // and the branding/config slice starts empty (`loading: true`) until it
        // loads. Concluding "no URL" while the config is still loading emits a
        // spurious error that flashes until the config populates and the fetch
        // recovers. Only conclude the URL is genuinely missing once the config
        // has also finished loading.
        return !configLoading;
      }),
      distinctUntilChanged(),
      switchMap(([ scriptUrl ]) => {

        if (!scriptUrl) {
          return of(quickJsActions.fetchScriptError({ error: new Error('No URL found in job or config')}));
        }

        return httpClient.get(scriptUrl, { responseType: 'text' }).pipe(
          switchMap(async (script) => {

            if (scriptUrl.includes('.ts')) {
              const { transform } = await import('sucrase');
              script = transform(script, {
                transforms: ['typescript'],
              }).code;
            }

            return quickJsActions.fetchScriptSuccess({ script })
          }),
          catchError((error) => of(quickJsActions.fetchScriptError({ error }))),
        );
      }),
    );
}, { functional: true, dispatch: true });

export const forceReloadScript = createEffect((
  store = inject(Store),
  actions$ = inject(Actions),
  httpClient = inject(HttpClient),
) => {
    return actions$.pipe(
      ofType(
        quickJsActions.reloadScriptButtonClicked,
        quickJsActions.reloadScriptCommandEntered,
      ),
      concatLatestFrom(() => store.select(selectCalculatorUrl)),
      switchMap(([ _action, scriptUrl ]) => {

        if (!scriptUrl) {
          return of(quickJsActions.fetchScriptError({ error: new Error('No URL found in config')}));
        }

        return httpClient.get(scriptUrl, { responseType: 'text' }).pipe(
          switchMap(async (script) => {

            if (scriptUrl.includes('.ts')) {
              const { transform } = await import('sucrase');
              script = transform(script, {
                transforms: ['typescript'],
              }).code;
            }

            return quickJsActions.fetchScriptSuccess({ script })
          }),
          catchError((error) => of(quickJsActions.fetchScriptError({ error }))),
        );
      }),
    );

}, { functional: true, dispatch: true });

export const runCalculations = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  quickJs = inject(QuickJsService),
) => {
  return actions$.pipe(
    ofType(
      quickJsActions.loadScriptSuccess,
      quickJsActions.inputChanged,
      // Multi-key edits must recalculate too. Without this, an `inputsChanged` dispatch updates
      // userInputs but never re-runs the calculator, leaving calculatedState (and therefore every
      // output the UI reads from it) stale until some other action happens to trigger a run.
      quickJsActions.inputsChanged,
      quickJsActions.inputReset,
      JobToolActions.jobLoaded,
      // May change weight
      NavigatorOutboxActions.jobInventoryUpdatedLocally,
      InventoryActions.itemAdded,
      InventoryActions.removeItemButtonClicked,
      InventoryActions.itemQuantityInputChanged,
      InventoryActions.itemEdited,
      JobCreateInventoryActions.itemQuantityInputChanged,
      JobCreateInventoryActions.removeItemButtonClicked,
      JobToolActions.jobDataReset,
      // Auto reset on error
      quickJsActions.runCalculationsError,
      // May change time and weight
      // Updated from Overview
      JobLocationsOverviewActions.calculateDistancesSuccess,
      JobLocationsOverviewActions.buildingTypeSelected,
      JobLocationsOverviewActions.sizeSelected,
      JobLocationsOverviewActions.accessSelected,
      JobLocationsOverviewActions.elevatorTypeSelected,
      JobLocationsOverviewActions.floorInputChanged,
      //Updated from Edit Job
      //Requirement: These actions are shared between New Opp V3 and Edit Job Mode
      //When locations details updated from Edit Mode, calculator receives updates on return to Job Page
      //and in New Opp V3 running calculation causes issue with adding locations details
      //JobCreateLocationsActions.buildingTypeSelected,
      //JobCreateLocationsActions.sizeSelected,
      //JobCreateLocationsActions.accessSelected,
      //JobCreateLocationsActions.elevatorTypeSelected,
      //JobCreateLocationsActions.floorInputChanged,
      //Changes discard, may affect time and weight
      JobCreateLocationsActions.jobLocationsLoaded,
      //Job type is updated
      JobToolActions.saveChangesSuccess,
      JobFormActionButtonActions.updateJobSuccess,
      JobToolSubscriptionActions.remoteJobUpdated,
      JobToolSubscriptionActions.localJobUpdated,
      // Inputs are cleared when navigating to the create-opportunity route
      routerNavigatedAction,
    ),
    // Filter out non-retriable errors to prevent infinite loops
    filter((action) => action.type !== quickJsActions.runCalculationsError.type || action.error instanceof RetriableError),
    // Only recalculate on navigation when entering the create-opportunity
    // route, so the cleared inputs are reflected in the rendered elements
    filter((action) => {
      if (action.type !== routerNavigatedAction.type) { return true; }

      const { urlAfterRedirects } = action.payload.event;

      return urlAfterRedirects.startsWith('/jobs/new');
    }),
    switchMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(selectScriptLoaded),
        store.select(quickjsFeature.selectUnflattenedInputs),
        store.select(selectJobDataForCalculator),
        store.select(authFeature.selectUser),
        store.select(brandingFeature.selectProducts),
        store.select(zonesFeature.selectCurrentAppZone),
        // Requirement: `#6517` - franchise options for the FP revenue split.
        store.select(selectSplitFranchises),
      ]),
      filter(([ _action, scriptLoaded ]) => scriptLoaded),
      map(([
        action,
        _scriptLoaded,
        userInputs,
        jobData,
        authedUser,
        products,
        sessionZone,
        availableFranchises,
      ]) => quickJs.runCalculations({
        userInputs,
        jobData: { ...jobData, availableFranchises },
        authedUser,
        products,
        sessionZone,
      }, {
        isReset: action.type === quickJsActions.runCalculationsError.type,
      })),
      map((payload) => quickJsActions.runCalculationsSuccess(payload)),
      catchError((error) => of(null).pipe(
        tap(() => {

          const activeElement = document.activeElement as HTMLElement;

          if (activeElement && typeof activeElement.blur === 'function') {
            activeElement.blur();
          }

        }),
        map(() => quickJsActions.runCalculationsError({ error })),
      )),
    )),
  );
}, { functional: true, dispatch: true });

export const runCalculatorButtonAction = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  quickJs = inject(QuickJsService),
) => {
  return actions$.pipe(
    ofType(quickJsActions.calculatorButtonClicked),
    switchMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(selectScriptLoaded),
        store.select(quickjsFeature.selectUnflattenedInputs),
        store.select(selectJobDataForCalculator),
        store.select(selectCalculatedState),
        store.select(selectElements),
        store.select(brandingFeature.selectProducts),
      ]),
      filter(([ _action, scriptLoaded ]) => scriptLoaded),
      map(([
        { action: functionName },
        _scriptLoaded,
        userInputs,
        jobData,
        calculatedState,
        elements,
        products,
      ]) => {
        if (!functionName) return;
        return quickJs.runButtonAction(functionName, {
          userInputs,
          jobData,
          calculatedState,
          elements,
          products,
        })
      }),
    )),
  );
}, { functional: true, dispatch: false });

export const updateJobScript = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(quickJsActions.scriptUrlEnteredForJob),
      exhaustMap(({ url }) => of(null).pipe(
        tap(() => {
          try {
            new URL(url);
          } catch {
            throw new Error(`Invald url: ${url}`);
          }
        }),
        concatLatestFrom(() => store.select(selectJob)),
        tap(([ _, job ]) => {
          if (!job) {
            throw new Error('Job not found');
          }
        }),
        mergeMap(([ _, job ]) => updateJobGQL.mutate({ updateJobs: {
          jobId: job.id,
          metadata: { calculatorUrl: url },
        }}).pipe(
          tap(() => console.log('Job script updated')),
          map(() => quickJsActions.setScriptOnJobSuccess({
            jobId: job.id,
            url,
          })),
        )),
        catchError((error) => {
          console.error('Failed to update job script', error);
          return of(quickJsActions.setScriptOnJobError({ error }));
        }),
      )),
    );
}, { functional: true, dispatch: true });

export const useCalculatorFromZoneSettings = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
  return actions$.pipe(
    ofType(JobHeaderActions.useCalculatorFromZoneSettingsMenuItemClicked),
    exhaustMap(() => of(null).pipe(
      concatLatestFrom(() => store.select(selectJobId)),
      mergeMap(([ _, jobId ]) => {
        return updateJobGQL.mutate({ updateJobs: {
          jobId,
          syncCalculatorFromConfig: true,
        }}).pipe(
          map((result) => {
            const url = result.data?.updateJobs?.jobs?.[0]?.metadata?.calculatorUrl as string;
            return quickJsActions.useCalculatorFromZoneSettingsSuccess({ jobId, url: url ?? '' });
          }),
        );
      }),
      catchError((error) => {
        console.error('Failed to sync calculator URL from config', error);
        return of(quickJsActions.useCalculatorFromZoneSettingsError({ error }));
      }),
    )),
  );
}, { functional: true, dispatch: true });

export const openBookEventDialog = createEffect((
   actions$ = inject(Actions),
   store = inject(Store),
) => {
     return actions$.pipe(
       ofType(
         OverviewTimelineActions.bookEventMenuItemSelected,
         OverviewTimelineActions.rescheduleEventMenuItemSelected,
         OverviewTimelineActions.bookEventButtonClicked,
         WorkOrdersActions.bookEventButtonClicked,
         WorkOrdersActions.scheduleEventMenuItemClicked,
         InventoryActions.createVirtualEstimateSuccess,
       ),
       concatLatestFrom((action) => [
         store.select(selectJob),
         store.select(createSelector(
           overviewTimelineFeature.selectTimelineActionsEventId,
           jobWorkordersFeature.selectWorkordersEditor,
           selectJob,
           (timelineActionsEventId, { actionsMenuEventId }, job) => {

             let eventId: string;

             if ('eventId' in action) {
               eventId = action.eventId;
             } else if (action.type === WorkOrdersActions.scheduleEventMenuItemClicked.type) {
               eventId = actionsMenuEventId;
             } else {
               eventId = timelineActionsEventId;
             }

             return (job.events || []).find((e) => e.id === eventId);
           },
         )),
         store.select(createConfigSelector('CALENDAREVENTS_ESTIMATELENGTH')),
         store.select(createConfigSelector('CALENDAREVENTS_VIRTUALESTIMATELENGTH')),
         store.select(createConfigSelector('CALENDAREVENTS_INCLUDEDOCKTRAVELTIME')),
       ]),
       tap(() => console.log('ran')),
       map(([
        _action,
        job,
        event,
        zoneEstimateLength,
        zoneVoseLength,
        zoneIncludeDockTravel,
      ]) => BookEventDialogActions.dialogOpened({
         job,
         event,
         zoneEstimateLength,
         zoneVoseLength,
         zoneIncludeDockTravel,
       })),
     );
}, { functional: true, dispatch: true });
